import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TenantDto } from '../types/tenant';
import type { ProjectDto } from '../types/project';
import { getTenantsFromToken, isOrganizationsClaimMissing } from '../auth/b2cOrganizations';
import { projectService } from '../services/projectService';
import { getModules } from '../services/appConfigService';
import { useAuthStore } from './authStore';
import { hasGlobalAccess } from '../utils/globalAccess';

/** Enabled feature modules (default all true until first fetch). */
export interface EnabledModules {
  automation: boolean;
  governance: boolean;
  integrations: boolean;
}

interface ContextState {
  currentTenant: TenantDto | null;
  currentProject: ProjectDto | null;
  availableTenants: TenantDto[];
  availableProjects: ProjectDto[];
  enabledModules: EnabledModules;
  globalAccessRoles: string[];
  isGlobalAccess: boolean;
  isLoading: boolean;
  error: string | null;
  setTenant: (tenant: TenantDto | null) => void;
  setProject: (project: ProjectDto | null) => void;
  loadTenants: () => Promise<void>;
  loadProjects: (tenantId: string) => Promise<void>;
  loadModules: () => Promise<void>;
  evaluateGlobalAccess: (token?: string | null) => void;
  clearContext: () => void;
}

/**
 * Context store for managing tenant and project state.
 */
export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      currentProject: null,
      availableTenants: [],
      availableProjects: [],
      enabledModules: { automation: true, governance: true, integrations: true },
      globalAccessRoles: [],
      isGlobalAccess: false,
      isLoading: false,
      error: null,

      setTenant: (tenant: TenantDto | null) => {
        set({ currentTenant: tenant, currentProject: null });
        // Clear projects when tenant changes
        set({ availableProjects: [] });
        // Load projects for new tenant if tenant is set
        if (tenant) {
          get().loadProjects(tenant.id);
        }
      },

      setProject: (project: ProjectDto | null) => {
        set({ currentProject: project });
      },

      /**
       * Builds the workspace list from the B2C token instead of `GET /tenants/mine`.
       *
       * WHY the token: CoordioCore reads the same `extension_Organizations` claim, and the claim's
       * tenant id is this app's tenant id (see `b2cOrganizations.ts`). Deriving both lists from one
       * claim is what lets a workspace selected in one product be recognised in the other; while
       * this came from the database the two products named the same workspace differently and
       * neither could honour the other's choice.
       *
       * It is also synchronous now. The previous call was the app's first request on every load,
       * so a slow or unavailable API left the switcher empty and every tenant-scoped page with no
       * context to fetch under. Claims are already in the MSAL cache by the time this runs.
       */
      loadTenants: async () => {
        const user = useAuthStore.getState().user;
        if (!user) {
          set({ error: 'User not authenticated' });
          return;
        }

        const tenants = getTenantsFromToken();

        // An absent claim is a directory configuration problem, not an empty membership list, and
        // the two need different fixes. Saying "no workspaces" for a missing claim sends the reader
        // to the wrong place, so the distinction is kept in the message.
        const error =
          tenants.length === 0 && isOrganizationsClaimMissing()
            ? 'Your sign-in carries no organizations claim, so no workspace can be selected. This is a B2C user-flow configuration issue, not an empty account.'
            : null;

        set({ availableTenants: tenants, isLoading: false, error });

        // Default to first tenant when none is selected.
        if (!get().currentTenant && tenants.length > 0) {
          get().setTenant(tenants[0]);
        }
      },

      loadProjects: async (tenantId: string) => {
        const user = useAuthStore.getState().user;
        if (!user) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const projects = await projectService.getUserProjects(tenantId);
          set({ availableProjects: projects, isLoading: false });
        } catch (error: any) {
          // Silently handle errors when backend is unavailable (500 errors)
          // Only set error for other types of errors
          const isBackendUnavailable = error.response?.status === 500;
          set({ 
            error: isBackendUnavailable ? null : (error.response?.data?.message || error.message || 'Failed to load projects'),
            isLoading: false,
            availableProjects: isBackendUnavailable ? [] : get().availableProjects,
          });
        }
      },

      loadModules: async () => {
        try {
          const modules = await getModules();
          set({
            enabledModules: {
              automation: modules.automation,
              governance: modules.governance,
              integrations: modules.integrations,
            },
            globalAccessRoles: modules.globalAccessRoles,
          });
          get().evaluateGlobalAccess(useAuthStore.getState().token);
          if (import.meta.env.DEV) {
            console.log('[contextStore] enabledModules loaded from API', modules);
          }
        } catch (err) {
          // Keep default (all true) when backend is unavailable
          if (import.meta.env.DEV) {
            console.warn('[contextStore] loadModules failed (menu will show all modules)', err);
          }
        }
      },

      evaluateGlobalAccess: (token?: string | null) => {
        const authToken = token ?? useAuthStore.getState().token;
        const allowedRoles = get().globalAccessRoles;
        set({ isGlobalAccess: hasGlobalAccess(authToken, allowedRoles) });
      },

      clearContext: () => {
        set({
          currentTenant: null,
          currentProject: null,
          availableTenants: [],
          availableProjects: [],
          error: null,
          isGlobalAccess: false,
          // Do not reset enabledModules; they are loaded from server on app init
        });
      },
    }),
    {
      name: 'agentops-context-storage',
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        currentProject: state.currentProject,
      }),
    }
  )
);
